"use client";

import { Suspense } from "react";
import { LoginAdminButton } from "@/app/login/_components/LoginAdminButton";
import { LoginHero } from "@/app/login/_components/LoginHero";
import { LoginNotices } from "@/app/login/_components/LoginNotices";
import { LoginProviderSection } from "@/app/login/_components/LoginProviderSection";
import AuthPageSuspenseFallback from "@/features/auth/components/AuthPageSuspenseFallback";
import UserPageShell from "@/components/layout/UserPageShell";
import { useLoginController } from "@/features/auth/hooks/useLoginController";

export default function Login() {
  return (
    <Suspense fallback={<AuthPageSuspenseFallback />}>
      <LoginInner />
    </Suspense>
  );
}

function LoginInner() {
  const {
    appleLoginError,
    appleLoginErrorTone,
    authError,
    azureError,
    checkingSession,
    handleAppleMusicLogin,
    handleOpenResearchLogin,
    handleSpotifyLogin,
    handleYouTubeLogin,
    heroMessage,
    isAppleLinkFlow,
    participantDisabledHint,
    participantGateMessage,
    participantGateTriggered,
    participantLoginDisabled,
    primeAppleMusicKit,
    showMobileBuilds,
  } = useLoginController();

  return (
    <UserPageShell
      width="wide"
      showGlow={false}
      className="theme-login-neutral overflow-y-auto"
      mainClassName="relative flex min-h-0 flex-1 items-center justify-center overflow-x-clip overflow-y-auto px-[clamp(0.65rem,1.8vw,1.5rem)] pb-[calc(clamp(0.65rem,2vh,1.2rem)+env(safe-area-inset-bottom))] pt-[calc(clamp(0.8rem,2.6vh,1.45rem)+env(safe-area-inset-top))] lg:overflow-clip"
    >
      <div className="fixed bottom-[calc(0.7rem+env(safe-area-inset-bottom))] right-[0.7rem] z-20 flex items-center gap-2 sm:bottom-[calc(0.8rem+env(safe-area-inset-bottom))] sm:right-[0.8rem]">
        <LoginAdminButton
          disabled={checkingSession}
          onClick={handleOpenResearchLogin}
        />
      </div>

      <div className="relative flex min-h-0 w-full flex-1 items-center justify-center overflow-x-clip overflow-y-auto">
        <div className="relative grid w-full max-w-[36rem] flex-none content-center grid-rows-[auto_auto] overflow-visible lg:min-h-0 lg:max-w-none lg:flex-1 lg:grid-cols-[minmax(0,0.94fr)_minmax(28rem,1.06fr)] lg:grid-rows-1 lg:overflow-hidden 2xl:grid-cols-[minmax(0,1.02fr)_minmax(29rem,0.98fr)]">
          <LoginHero rotatingMessage={heroMessage} />

          <section
            className="theme-login-divider flex min-h-0 w-full min-w-0 flex-col overflow-hidden border-t lg:border-l lg:border-t-0"
          >
            <div className="flex min-h-0 flex-1 flex-col justify-center overflow-visible px-[clamp(0.9rem,1.8vw,1rem)] py-[clamp(0.95rem,2.2vh,1.1rem)] lg:items-center lg:justify-center lg:overflow-y-auto lg:px-[clamp(1.25rem,2vw,1.9rem)] lg:py-[clamp(1.25rem,3vh,2rem)] 2xl:px-[clamp(2rem,2vw,2.75rem)] 2xl:py-[clamp(2.25rem,3vh,3rem)]">
              <div className="w-full lg:max-w-[30rem]">
                <LoginNotices
                  authError={authError}
                  appleLoginError={appleLoginError}
                  appleLoginErrorTone={appleLoginErrorTone}
                  azureError={azureError}
                  participantGateTriggered={participantGateTriggered}
                  participantGateMessage={participantGateMessage}
                  isAppleLinkFlow={isAppleLinkFlow}
                />

                <LoginProviderSection
                  participantLoginDisabled={participantLoginDisabled}
                  participantDisabledHint={participantDisabledHint}
                  showMobileBuilds={showMobileBuilds}
                  checkingSession={checkingSession}
                  onPrimeAppleMusicKit={primeAppleMusicKit}
                  onAppleMusicLogin={() => {
                    void handleAppleMusicLogin();
                  }}
                  onYouTubeLogin={() => {
                    void handleYouTubeLogin();
                  }}
                  onSpotifyLogin={() => {
                    void handleSpotifyLogin();
                  }}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </UserPageShell>
  );
}
